import { icons } from '../icons';
import React, { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  fadeTransition,
  overlayBackdrop,
  popTransition,
  useShownWhileOpen,
} from './overlay';

type KoobModalProps = {
  open: boolean;
  setOpen?: (open: boolean) => void;
  title?: React.ReactNode;
  widthClass?: string;
  paddingClass?: string;
  overflow?: 'hidden' | 'visible';
  children?: React.ReactNode;
  allowClose?: boolean;
  showCloseIcon?: boolean;
  autoFocus?: boolean;
  initialFocus?: React.MutableRefObject<HTMLElement | null>;
};

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

export default function KoobModal({
  open,
  setOpen,
  // Renders the standard dialog heading. Prefer it over hand-rolling one at
  // the call site — that is how modal titles drifted to three different sizes.
  title,
  widthClass = 'sm:max-w-xl',
  // Only override for full-bleed content ('p-0'); a titled modal needs the
  // default padding to sit right.
  paddingClass = 'p-5 sm:p-7',
  overflow = 'hidden',
  children,
  allowClose = true,
  // Opt-in: call sites that render their own header already have a close
  // affordance, so a second icon would double up with theirs. A titled modal
  // gets one by default — every closable modal must have one.
  showCloseIcon = Boolean(title),
  autoFocus = true,
  initialFocus,
}: KoobModalProps) {
  const close = () => {
    if (setOpen && allowClose) {
      setOpen(false);
    }
  };

  const noFocusRef = useRef<HTMLDivElement>(null);
  const { title: shownTitle, children: shownChildren } = useShownWhileOpen(
    open,
    { title, children },
  );
  const resolvedInitialFocus =
    initialFocus ?? (autoFocus ? undefined : noFocusRef);

  return (
    // Portaled, but React still bubbles the modal's clicks to whatever
    // rendered it (a clickable card). The stop cannot live inside the Dialog:
    // on an outside click Headless UI closes it from a document listener, and
    // React 17 unmounts it before handling that same click, so React starts
    // from the nearest mounted ancestor and skips every handler inside. This
    // span stays mounted; `contents` keeps it out of the opener's layout.
    <span className="contents" onClick={stopPropagation}>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-modal"
          onClose={() => close()}
          initialFocus={resolvedInitialFocus}
        >
          <Transition.Child as={Fragment} {...fadeTransition}>
            <div className={`fixed inset-0 ${overlayBackdrop}`} />
          </Transition.Child>

          <div className="fixed inset-0 z-modal overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center">
              <Transition.Child as={Fragment} {...popTransition}>
                <Dialog.Panel
                  className={`relative transform ${overflow === 'hidden' ? 'overflow-hidden' : 'overflow-visible'} rounded-2xl bg-white text-left text-k-body shadow-[0_24px_60px_rgba(15,26,46,.35)] transition-all w-full min-w-0 sm:my-8 ${widthClass} ${paddingClass}`}
                >
                  {!autoFocus && !initialFocus && (
                    <div
                      ref={noFocusRef}
                      tabIndex={-1}
                      className="sr-only focus:outline-none"
                      aria-hidden="true"
                    />
                  )}
                  {showCloseIcon && allowClose && (
                    <button
                      type="button"
                      onClick={close}
                      aria-label="Close"
                      className="absolute z-over-sticky top-4 right-4 sm:top-6 sm:right-6 flex size-8 items-center justify-center rounded-lg border-none bg-transparent cursor-pointer text-k-p hover:bg-navy-50 hover:text-k-h"
                    >
                      <i className={icons.close} />
                    </button>
                  )}
                  {shownTitle && (
                    <h2 className="m-0 mb-4 pr-10 text-lg font-semibold text-k-h">
                      {shownTitle}
                    </h2>
                  )}
                  {shownChildren}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </span>
  );
}
